import React from 'react';

import { Navigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Link } from '@mui/material';
import { LoginForm } from '../../components/LoginForm';
import { useAuth } from "../../contexts/AuthContext";

export const LoginPage: React.FC = () => {
  const { login, isLoading, error, clearError, isAuthenticated, requiresNewPassword, requiresPasswordReset, resetUsername, sessionExpired } = useAuth();

  const [searchParams] = useSearchParams();

  // Get the 'from' parameter, default to '/' if not found
  const from = searchParams.get('from') || '/';

  // If user is already authenticated, redirect to the default page
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  // If user needs to set a new password, redirect to new password page
  if (requiresNewPassword) {
    return <Navigate to="/new-password" replace />;
  }

  // If user needs to complete a password reset (verification code flow)
  if (requiresPasswordReset) {
    const params = resetUsername ? `?email=${encodeURIComponent(resetUsername)}` : '';
    return <Navigate to={`/reset-password${params}`} replace />;
  }

  return (
    <Box minHeight="100vh" bgcolor="background.default" py={4}>
      <Container maxWidth="lg">
        <Box textAlign="center" mb={4}>
          <Typography variant="h3" component="h1" color="text.primary" gutterBottom>
            Tax Assist by Harness
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Please sign in to continue
          </Typography>
          {sessionExpired && (
            <Box
              mt={2}
              p={2}
              bgcolor="warning.light"
              borderRadius={1}
              border={1}
              borderColor="warning.main"
            >
              <Typography variant="body1" color="warning.dark" fontWeight="medium">
                Your session has expired. Please sign in again.
              </Typography>
            </Box>
          )}
        </Box>
        <LoginForm
          onLogin={login}
          isLoading={isLoading}
          error={error}
          onClearError={clearError}
        />
        <Box textAlign="center" mt={2}>
          <Link component={RouterLink} to="/reset-password" variant="body2">
            Have a password reset code?
          </Link>
        </Box>
      </Container>
    </Box>
  );
};
